import {
  FETCH_ESTIMATE,
  FETCH_ESTIMATE_SUCCESS,
  FETCH_ESTIMATE_FAILED,
  CLEAR_ESTIMATE
} from "../store/types";
import Polyline from "@mapbox/polyline";
import { firebase } from "../config/configureFirebase";
import { FareCalculator } from "../other/FareCalculator";
import { onValue } from "firebase/database";
import store from "../store/store";
import {
  getReturnDistanceToVienna,
  checkOutsideViennaSurcharge,
  SURCHARGES
} from "../other/ViennaTariffRules";

/**
 * Extract lat/lng safely from Web + React Native structures
 */
const extractLatLng = (place) => {
  if (!place) return null;

  const lat =
    place.lat ??
    place.coords?.lat ??
    place.coords?.latitude ??
    place.latitude ??
    place.location?.lat ??
    place.location?.latitude;

  const lng =
    place.lng ??
    place.coords?.lng ??
    place.coords?.longitude ??
    place.longitude ??
    place.location?.lng ??
    place.location?.longitude;

  if (lat == null || lng == null) return null;

  const parsedLat = parseFloat(lat);
  const parsedLng = parseFloat(lng);

  if (Number.isNaN(parsedLat) || Number.isNaN(parsedLng)) return null;

  return { lat: parsedLat, lng: parsedLng };
};

// ✅ SAFE: Get valid promo from Redux store
const getValidPromo = () => {
  try {
    const state = store.getState();
    const promo = state?.auth?.profile?.activePromo;

    if (
      promo &&
      typeof promo === 'object' &&
      promo.expiresAt &&
      Number(promo.expiresAt) > Date.now()
    ) {
      return promo;
    }

    return null;
  } catch {
    return null;
  }
};

export const getEstimate = (bookingData) => async (dispatch) => {
  const { settingsRef } = firebase;

  dispatch({
    type: FETCH_ESTIMATE,
    payload: bookingData,
  });

  const res = bookingData.routeDetails;

  if (!res) {
    dispatch({
      type: FETCH_ESTIMATE_FAILED,
      payload: "No Route Found",
    });
    return;
  }

  const points = Polyline.decode(res.polylinePoints);
  const waypoints = points.map(p => ({
    latitude: p[0],
    longitude: p[1],
  }));

  onValue(settingsRef, (settingdata) => {
    const settings = settingdata.val();

    const distance = settings.convert_to_mile
      ? res.distance_in_km / 1.609344
      : res.distance_in_km;

    let returnDistance = 0;
    let additionalSurcharge = 0;
    let nearAirport = false;
    let outsideVienna = false;

    const pickupCoords = extractLatLng(bookingData.pickup);
    const dropCoords = extractLatLng(bookingData.drop);

    const pickupCheck = pickupCoords
      ? checkOutsideViennaSurcharge(pickupCoords.lat, pickupCoords.lng)
      : null;

    const dropCheck = dropCoords
      ? checkOutsideViennaSurcharge(dropCoords.lat, dropCoords.lng)
      : null;

    let refPoint = null;

    if (pickupCheck?.nearAirport || dropCheck?.nearAirport) {
      nearAirport = true;
      additionalSurcharge = SURCHARGES.AIRPORT_SURCHARGE;
      refPoint = dropCheck?.nearAirport ? dropCoords : pickupCoords;
    } else if (pickupCheck?.outsideVienna || dropCheck?.outsideVienna) {
      outsideVienna = true;
      additionalSurcharge = SURCHARGES.OUTSIDE_VIENNA_RETURN;
      refPoint = dropCheck?.outsideVienna ? dropCoords : pickupCoords;
    }

    if (refPoint) {
      returnDistance = getReturnDistanceToVienna(refPoint.lat, refPoint.lng);
    }

    const totalDistance = distance + returnDistance;

    // ───────────────────────────────────────
    // 🕐 TRIP DATE HANDLING
    // ───────────────────────────────────────
    let tripStartTime;

    if (bookingData.tripdate) {
      const timestamp =
        typeof bookingData.tripdate === 'string'
          ? parseInt(bookingData.tripdate, 10)
          : bookingData.tripdate;

      tripStartTime = new Date(timestamp);
    } else {
      tripStartTime = new Date();
    }

    const ONE_HOUR_MS = 60 * 60 * 1000;
    const isReservation =
      tripStartTime.getTime() - Date.now() >= ONE_HOUR_MS;

    const validPromo = isReservation ? getValidPromo() : null;

    // ───────────────────────────────────────
    // 💰 FARE CALCULATION
    // ───────────────────────────────────────
    let calc;
    try {
      calc = FareCalculator(
        distance,
        res.time_in_secs,
        bookingData.carDetails,
        bookingData.instructionData,
        settings.decimal,
        tripStartTime,
        returnDistance,
        additionalSurcharge,
        validPromo
      );
    } catch {
      dispatch({
        type: FETCH_ESTIMATE_FAILED,
        payload: "Fare calculation failed",
      });
      return;
    }

    const {
      totalCost = 0,
      grandTotal = 0,
      convenience_fees = 0,
      tariffApplied = "Tagtarif",
      promoApplied = null
    } = calc || {};

    dispatch({
      type: FETCH_ESTIMATE_SUCCESS,
      payload: {
        pickup: bookingData.pickup,
        drop: bookingData.drop,
        carDetails: bookingData.carDetails,
        instructionData: bookingData.instructionData,

        estimateDistance: Number(distance.toFixed(settings.decimal)),
        returnDistance: Number(returnDistance.toFixed(settings.decimal)),
        totalDistance: Number(totalDistance.toFixed(settings.decimal)),

        outsideVienna,
        nearAirport,
        additionalSurcharge: Number(additionalSurcharge.toFixed(settings.decimal)),

        fareCost: Number(totalCost.toFixed(settings.decimal)),
        estimateFare: Number(grandTotal.toFixed(settings.decimal)),
        estimateTime: res.time_in_secs,
        convenience_fees: Number(convenience_fees.toFixed(settings.decimal)),
        tariffApplied,

        promo: promoApplied && typeof promoApplied === 'object'
          ? {
              code: promoApplied.code || null,
              percent: promoApplied.percent || 0,
              discount: promoApplied.discount || 0,
              maxDiscount: promoApplied.maxDiscount || 0,
            }
          : null,

        waypoints,
      },
    });
  }, { onlyOnce: true });
};

export const clearEstimate = () => (dispatch) => {
  dispatch({
    type: CLEAR_ESTIMATE,
    payload: null,
  });
};
